import { AgGridTooltipWrapper } from "$root/third-party-integrations/ag-grid/ag-grid";
import { Text } from "@mdotm/shadcn/legacy/components";
import type { ColDef, EditableCallbackParams } from "ag-grid-community";
import type { CustomTooltipProps } from "ag-grid-react";

const DEFAULT_MESSAGE = "You don't have permission to edit this cell";

/**
 * Wraps a tooltipComponent with a permission check.
 * If the cell is not editable (as determined by `editable`), shows a permission-denied message.
 * Otherwise, delegates to the inner tooltip (if any).
 *
 * Usage:
 *   colDef.tooltipComponent = withPermissionTooltip(editableHelper, colDef.tooltipComponent);
 */
export function withPermissionTooltip<T, U = unknown>(
	editable: ColDef<T>["editable"],
	inner?: (props: CustomTooltipProps<T, U>) => React.JSX.Element | string,
	options?: { message?: string },
): (props: CustomTooltipProps<T, U>) => React.JSX.Element | string {
	return function PermissionTooltip(props: CustomTooltipProps<T, U>): React.JSX.Element | string {
		const canEdit =
			typeof editable === "function"
				? (editable as (params: EditableCallbackParams<T>) => boolean)({
						data: props.data,
						node: props.node,
						column: props.column,
						colDef: props.colDef,
					} as EditableCallbackParams<T>)
				: (editable ?? false);

		if (!canEdit) {
			return (
				<AgGridTooltipWrapper>
					<Text type="Body/M/Book">{options?.message ?? DEFAULT_MESSAGE}</Text>
				</AgGridTooltipWrapper>
			);
		}

		if (inner != null) {
			const Inner = inner;
			return <Inner {...props} />;
		}
		return "";
	};
}
